/**
 * KPI Service - RTK Query API
 *
 * Provides React hooks for fetching KPI data from the backend.
 * Supports 3-level hierarchy: L1 (Company), L2 (Unit), L3 (Room)
 *
 * Available hooks:
 * - useGetFinancialKpisQuery: Financial metrics (margins, Cashflow, RevPAR, etc.)
 * - useGetOperationalKpisQuery: Operational metrics (occupancy, vacant days)
 * - useGetGrowthKpisQuery: Growth metrics (revenue growth, CAC)
 * - useGetUtilitiesKpisQuery: Utilities breakdown by provider/property
 * - useGetExpenseBreakdownQuery: Expense breakdown by category
 * - useGetPropertiesQuery: Property list for filters
 *
 * Created: 2026-03-28
 * Part of KPI V1 Implementation
 */

import { createApi } from "@reduxjs/toolkit/query/react";
import { baseRestQueryResult as baseQuery } from "@/services/api";
import {
    FinancialKpisRes,
    OperationalKpisRes,
    GrowthKpisRes,
    BatchGrowthKpiRequest,
    BatchGrowthKpiResponse,
    UtilitiesKpisRes,
    BatchUtilitiesKpiRequest,
    BatchUtilitiesKpiResponse,
    ExpenseBreakdownRes,
    KpiParams,
    UtilitiesKpiParams,
    ExpenseBreakdownParams,
    PropertyInfo,
    KpiUnitRoomsResponse,
    BatchFinancialKpiRequest,
    BatchFinancialKpiResponse,
    BatchCompanyOperationalKpiRequest,
    BatchCompanyOperationalKpiResponse,
    BatchOperationalKpiRequest,
    BatchOperationalKpiResponse,
    BatchRoomFinancialKpiRequest,
    BatchRoomFinancialKpiResponse,
    BatchRoomOperationalKpiRequest,
    BatchRoomOperationalKpiResponse,
} from "@/types/kpi.type";

export const kpiApi = createApi({
    reducerPath: "kpiApi",
    baseQuery,
    endpoints: (builder) => ({
        /**
         * Get financial KPIs
         * Endpoint: GET /api/v1/kpi/financial
         */
        getFinancialKpis: builder.query<FinancialKpisRes, Partial<KpiParams>>({
            query: (params) => ({
                url: `kpi/financial`,
                params: {
                    range: params.range || 'last_12_months',
                    start: params.start || undefined,
                    end: params.end || undefined,
                    level: params.level || 'L1',
                    business_unit_id: params.business_unit_id || undefined,
                    room_id: params.room_id || undefined,
                },
            }),
            keepUnusedDataFor: 60,
        }),

        /**
         * Batch financial KPIs - fetch multiple units and ranges in one call
         * Endpoint: POST /api/v1/kpi/financial/batch-units
         */
        getBatchFinancialKpis: builder.mutation<BatchFinancialKpiResponse, BatchFinancialKpiRequest>({
            query: (body) => ({
                url: `kpi/financial/batch-units`,
                method: 'POST',
                body,
            }),
        }),
        getBatchFinancialKpisData: builder.query<BatchFinancialKpiResponse, BatchFinancialKpiRequest>({
            query: (body) => ({
                url: `kpi/financial/batch-units`,
                method: 'POST',
                body,
            }),
            keepUnusedDataFor: 300,
        }),

        /**
         * Batch room financial KPIs - fetch multiple rooms and ranges in one call (L3)
         * Endpoint: POST /api/v1/kpi/financial/batch-rooms
         * Used when expanding a unit row to see RevPAR/ARPR by room.
         */
        getBatchRoomFinancialKpis: builder.mutation<BatchRoomFinancialKpiResponse, BatchRoomFinancialKpiRequest>({
            query: (body) => ({
                url: `kpi/financial/batch-rooms`,
                method: 'POST',
                body,
            }),
        }),
        getBatchRoomFinancialKpisData: builder.query<BatchRoomFinancialKpiResponse, BatchRoomFinancialKpiRequest>({
            query: (body) => ({
                url: `kpi/financial/batch-rooms`,
                method: 'POST',
                body,
            }),
            keepUnusedDataFor: 300,
        }),

        /**
         * Get operational KPIs
         * Endpoint: GET /api/v1/kpi/operational
         */
        getOperationalKpis: builder.query<OperationalKpisRes, Partial<KpiParams>>({
            query: (params) => ({
                url: `kpi/operational`,
                params: {
                    range: params.range || 'last_12_months',
                    start: params.start || undefined,
                    end: params.end || undefined,
                    level: params.level || 'L1',
                    business_unit_id: params.business_unit_id || undefined,
                    room_id: params.room_id || undefined,
                },
            }),
            keepUnusedDataFor: 60,
        }),

        /**
         * Batch company operational KPIs - fetch company-level ranges in one call (L1)
         * Endpoint: POST /api/v1/kpi/operational/batch-company
         * Used when drilldowns need maintenance history across multiple years.
         */
        getBatchCompanyOperationalKpis: builder.mutation<BatchCompanyOperationalKpiResponse, BatchCompanyOperationalKpiRequest>({
            query: (body) => ({
                url: `kpi/operational/batch-company`,
                method: 'POST',
                body,
            }),
        }),
        getBatchCompanyOperationalKpisData: builder.query<BatchCompanyOperationalKpiResponse, BatchCompanyOperationalKpiRequest>({
            query: (body) => ({
                url: `kpi/operational/batch-company`,
                method: 'POST',
                body,
            }),
            keepUnusedDataFor: 300,
        }),

        /**
         * Batch operational KPIs - fetch multiple units and ranges in one call (L2)
         * Endpoint: POST /api/v1/kpi/operational/batch-units
         * Used when drilldowns need occupancy by unit across multiple years.
         */
        getBatchOperationalKpis: builder.mutation<BatchOperationalKpiResponse, BatchOperationalKpiRequest>({
            query: (body) => ({
                url: `kpi/operational/batch-units`,
                method: 'POST',
                body,
            }),
        }),
        getBatchOperationalKpisData: builder.query<BatchOperationalKpiResponse, BatchOperationalKpiRequest>({
            query: (body) => ({
                url: `kpi/operational/batch-units`,
                method: 'POST',
                body,
            }),
            keepUnusedDataFor: 300,
        }),

        /**
         * Batch room operational KPIs - fetch multiple rooms and ranges in one call (L3)
         * Endpoint: POST /api/v1/kpi/operational/batch-rooms
         * Used when drilldowns need vacant days by room across multiple years.
         */
        getBatchRoomOperationalKpis: builder.mutation<BatchRoomOperationalKpiResponse, BatchRoomOperationalKpiRequest>({
            query: (body) => ({
                url: `kpi/operational/batch-rooms`,
                method: 'POST',
                body,
            }),
        }),
        getBatchRoomOperationalKpisData: builder.query<BatchRoomOperationalKpiResponse, BatchRoomOperationalKpiRequest>({
            query: (body) => ({
                url: `kpi/operational/batch-rooms`,
                method: 'POST',
                body,
            }),
            keepUnusedDataFor: 300,
        }),

        /**
         * Get growth KPIs
         * Endpoint: GET /api/v1/kpi/growth
         */
        getGrowthKpis: builder.query<GrowthKpisRes, Partial<KpiParams>>({
            query: (params) => ({
                url: `kpi/growth`,
                params: {
                    range: params.range || 'last_12_months',
                    start: params.start || undefined,
                    end: params.end || undefined,
                    level: params.level || 'L1',
                    business_unit_id: params.business_unit_id || undefined,
                },
            }),
            keepUnusedDataFor: 60,
        }),

        /**
         * Batch growth KPIs - fetch multiple units and ranges in one call (L2)
         * Endpoint: POST /api/v1/kpi/growth/batch-units
         */
        getBatchGrowthKpis: builder.mutation<BatchGrowthKpiResponse, BatchGrowthKpiRequest>({
            query: (body) => ({
                url: `kpi/growth/batch-units`,
                method: 'POST',
                body,
            }),
        }),
        getBatchGrowthKpisData: builder.query<BatchGrowthKpiResponse, BatchGrowthKpiRequest>({
            query: (body) => ({
                url: `kpi/growth/batch-units`,
                method: 'POST',
                body,
            }),
            keepUnusedDataFor: 300,
        }),

        /**
         * Get utilities KPIs
         * Endpoint: GET /api/v1/kpi/utilities
         */
        getUtilitiesKpis: builder.query<UtilitiesKpisRes, Partial<UtilitiesKpiParams>>({
            query: (params) => {
                const queryParams: Record<string, any> = {
                    range: params.range || 'last_12_months',
                };

                if (params.start) queryParams.start = params.start;
                if (params.end) queryParams.end = params.end;

                // Handle array parameters
                if (params.property_ids && params.property_ids.length > 0) {
                    queryParams['property_ids'] = params.property_ids;
                }
                if (params.providers && params.providers.length > 0) {
                    queryParams['providers'] = params.providers;
                }

                return {
                    url: `kpi/utilities`,
                    params: queryParams,
                };
            },
            keepUnusedDataFor: 60,
        }),

        getBatchUtilitiesKpis: builder.mutation<BatchUtilitiesKpiResponse, BatchUtilitiesKpiRequest>({
            query: (body) => ({
                url: `kpi/utilities/batch-company`,
                method: 'POST',
                body,
            }),
        }),
        getBatchUtilitiesKpisData: builder.query<BatchUtilitiesKpiResponse, BatchUtilitiesKpiRequest>({
            query: (body) => ({
                url: `kpi/utilities/batch-company`,
                method: 'POST',
                body,
            }),
            keepUnusedDataFor: 300,
        }),

        /**
         * Get expense breakdown
         * Endpoint: GET /api/v1/kpi/expenses
         */
        getExpenseBreakdown: builder.query<ExpenseBreakdownRes, Partial<ExpenseBreakdownParams>>({
            query: (params) => {
                const queryParams: Record<string, any> = {
                    range: params.range || 'last_12_months',
                };

                if (params.start) queryParams.start = params.start;
                if (params.end) queryParams.end = params.end;
                if (params.scope) queryParams.scope = params.scope;
                if (params.dimension) queryParams.dimension = params.dimension;
                if (params.business_unit_id) queryParams.business_unit_id = params.business_unit_id;
                if (params.types && params.types.length > 0) {
                    // Join array as comma-separated string - backend will parse it
                    queryParams.types = params.types.join(',');
                }
                if (params.codes && params.codes.length > 0) {
                    queryParams.codes = params.codes.join(',');
                }

                return {
                    url: `kpi/expenses`,
                    params: queryParams,
                };
            },
            keepUnusedDataFor: 60,
        }),

        /**
         * Get properties list for filters
         * Endpoint: GET /api/v1/kpi/properties
         */
        getProperties: builder.query<PropertyInfo[], void>({
            query: () => ({
                url: `kpi/properties`,
            }),
            keepUnusedDataFor: 300, // Cache for 5 minutes
        }),

        getKpiUnitRooms: builder.query<KpiUnitRoomsResponse, string>({
            query: (id) => ({
                url: `kpi/units/${id}/rooms`,
            }),
            keepUnusedDataFor: 300,
        }),
    }),
});

// Export auto-generated hooks
export const {
    useGetFinancialKpisQuery,
    useLazyGetFinancialKpisQuery,
    useGetBatchFinancialKpisMutation,
    useGetBatchFinancialKpisDataQuery,
    useGetBatchRoomFinancialKpisMutation,
    useGetBatchRoomFinancialKpisDataQuery,
    useGetBatchCompanyOperationalKpisMutation,
    useGetBatchCompanyOperationalKpisDataQuery,
    useGetBatchOperationalKpisMutation,
    useGetBatchOperationalKpisDataQuery,
    useGetBatchRoomOperationalKpisMutation,
    useGetBatchRoomOperationalKpisDataQuery,
    useGetOperationalKpisQuery,
    useLazyGetOperationalKpisQuery,
    useGetGrowthKpisQuery,
    useLazyGetGrowthKpisQuery,
    useGetBatchGrowthKpisMutation,
    useGetBatchGrowthKpisDataQuery,
    useGetUtilitiesKpisQuery,
    useGetBatchUtilitiesKpisMutation,
    useGetBatchUtilitiesKpisDataQuery,
    useGetExpenseBreakdownQuery,
    useGetPropertiesQuery,
    useGetKpiUnitRoomsQuery,
    usePrefetch,
} = kpiApi;
